import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Upload } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useLanguage } from "@/shared/hooks/use-language";
import { useToast } from "@/shared/hooks/use-toast";
import { labelOr } from "@/shared/lib/label";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import { exitToLogin } from "@/shared/lib/session-exit";
import type { ScopeContext } from "@/features/auth";
import { accountScopeOf } from "@/features/portfolio/api/portfolio";
import { useUserProfile } from "@/features/auth/model/use-user-profile";
import { isSavedSetting, type UserSetting } from "@/shared/lib/user-setting";
import { DEFAULT_NAV_CURRENCY } from "@/shared/lib/nav-currency";
import { MAX_LISTED_POSITIONS, type Position } from "@/features/portfolio/api/position";
import {
  getPositionSnapshotById,
  SETTLED_SNAPSHOT_STATUSES,
  type PositionSnapshot,
} from "@/features/portfolio/api/position-snapshot";
import { type ExternalTradeOrder } from "@/features/portfolio/api/external-trade";
import {
  portfolioDetailQueryKeys,
  usePortfolioQuery,
  usePositionsQuery,
  usePositionSummaryQuery,
  useExternalTradesQuery,
} from "@/features/portfolio/hooks/use-portfolio-detail-queries";
import { useCsvImport } from "@/features/portfolio/hooks/use-csv-import";
import { type ParseRequestType } from "@/features/portfolio/api/parse-request";
import { useBookingStatus } from "@/features/portfolio/hooks/use-booking-status";
import {
  usePortfolioValuationQuery,
  type NavHistoryPeriod,
} from "@/features/portfolio/hooks/use-portfolio-valuation";
import { NavHeader } from "./components/NavHeader";
import { NavHistoryChart } from "./components/NavHistoryChart";
import { PositionsTable } from "./components/PositionsTable";
import { AllocationDonut } from "./components/AllocationDonut";
import { OperationsTab } from "./components/OperationsTab";
import { ImportPanel } from "./components/ImportPanel";
import { DeclareSnapshotDialog } from "./components/DeclareSnapshotDialog";
import { BookingMarker } from "./components/BookingMarker";

const DEFAULT_BLOTTER_ORDER: ExternalTradeOrder = "-reference_time";
// A year of business days is about 250 points, three pages at most: enough to read a
// trend from, without walking a fund's whole life on every open. "ALL" is one click away.
const DEFAULT_NAV_HISTORY_PERIOD: NavHistoryPeriod = "1Y";
const SNAPSHOT_POLL_MS = 1500;
const SNAPSHOT_SETTLE_TIMEOUT_MS = 30000;
const PROFILE_CURRENCY_WAIT_MS = 3000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const navCurrencyFromSetting = (setting: UserSetting | null): string =>
  (setting && isSavedSetting(setting) ? setting.currency : null) ?? DEFAULT_NAV_CURRENCY;

// The one place that ties a tab to the import type it opens. The tab follows the open
// import, and the open import follows the tab until its upload starts.
const IMPORT_TYPE_BY_TAB = {
  positions: "position-parse-request",
  operations: "trade-parse-request",
} as const satisfies Record<string, ParseRequestType>;
type PortfolioTab = keyof typeof IMPORT_TYPE_BY_TAB;
const PORTFOLIO_TABS = Object.keys(IMPORT_TYPE_BY_TAB) as PortfolioTab[];
const isPortfolioTab = (value: string): value is PortfolioTab =>
  (PORTFOLIO_TABS as string[]).includes(value);
// The inverse of `IMPORT_TYPE_BY_TAB`, typed from it: each key's only legal value is the
// tab that map sends to that type. A `ParseRequestType` with no tab types as `never`, so
// tsc refuses the map until that type has a tab.
type TabByImportType = {
  [Type in ParseRequestType]: {
    [Tab in PortfolioTab]: (typeof IMPORT_TYPE_BY_TAB)[Tab] extends Type ? Tab : never;
  }[PortfolioTab];
};
const TAB_BY_IMPORT_TYPE: TabByImportType = {
  "position-parse-request": "positions",
  "trade-parse-request": "operations",
};

interface BlotterQuery {
  order: ExternalTradeOrder;
  showsCanceled: boolean;
}

const PortfolioDetail = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  // `getScopeContext` parses the stored record fresh on every call, so reading it
  // directly in the render body hands back a new object identity every render even
  // when nothing in storage changed. Memoized on `sessionId` alone — the only way the
  // stored record legitimately changes under this page is a scope switch, which hard-
  // reloads the whole app (see frontend/overview.md), so a stable `sessionId` means a
  // stable record. Without this, `accountScope` below inherits the same instability,
  // and anything that depends on it in an effect's dependency array (`useCsvImport`'s
  // dispatch effect) re-fires on every unrelated re-render of this page.
  const scopeContext: ScopeContext | null = useMemo(
    () => (sessionId ? ScopeContextStorage.getScopeContext(sessionId) : null),
    [sessionId],
  );
  const accountScope = useMemo(
    () => (scopeContext ? accountScopeOf(scopeContext) : null),
    [scopeContext],
  );

  const queryClient = useQueryClient();
  const [blotterQuery, setBlotterQuery] = useState<BlotterQuery>({
    order: DEFAULT_BLOTTER_ORDER,
    showsCanceled: false,
  });
  const [snapshotTarget, setSnapshotTarget] = useState<Position | null>(null);
  const profile = useUserProfile();
  const [seededCurrency, setSeededCurrency] = useState<string | null>(() =>
    profile.hasAnswered ? navCurrencyFromSetting(profile.setting) : null,
  );
  const [profileWaitElapsed, setProfileWaitElapsed] = useState(false);
  useEffect(() => {
    if (seededCurrency !== null) return;
    const timer = setTimeout(() => setProfileWaitElapsed(true), PROFILE_CURRENCY_WAIT_MS);
    return () => clearTimeout(timer);
  }, [seededCurrency]);
  if (seededCurrency === null && (profile.hasAnswered || profileWaitElapsed)) {
    setSeededCurrency(profile.hasAnswered ? navCurrencyFromSetting(profile.setting) : DEFAULT_NAV_CURRENCY);
  }
  const [chosenCurrency, setChosenCurrency] = useState<string | null>(null);
  const requestedCurrency = chosenCurrency ?? seededCurrency;
  const [navHistoryPeriod, setNavHistoryPeriod] = useState<NavHistoryPeriod>(DEFAULT_NAV_HISTORY_PERIOD);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const scopeMissing = !sessionId || !accountScope;

  const portfolioQuery = usePortfolioQuery(sessionId, accountScope, portfolioId);
  const positionsQuery = usePositionsQuery(sessionId, accountScope, portfolioId);
  const summaryQuery = usePositionSummaryQuery(sessionId, accountScope, portfolioId, requestedCurrency);
  const externalTradesQuery = useExternalTradesQuery(
    sessionId, accountScope, portfolioId, blotterQuery.order, blotterQuery.showsCanceled,
  );

  const portfolio = portfolioQuery.data ?? null;
  const positions = positionsQuery.data?.positions ?? [];
  const summary = summaryQuery.summary;
  const trades = externalTradesQuery.data?.trades ?? [];
  // The read follows the cursor to its end, so an outstanding cursor is no longer what
  // "truncated" means: this is the walk having seen a row it did not list, the one case
  // where rows the portfolio holds are genuinely absent from the table and from the
  // ranking over it. A portfolio that ends exactly on the stop claims nothing.
  const positionsTruncated = positionsQuery.data?.isTruncated ?? false;
  const tradesTruncated = Boolean(externalTradesQuery.data?.cursor);

  // TanStack keeps the last successful `data` through a failed *background* refetch of
  // the same key — `isError` alone does not mean there is nothing to show. Each section
  // below splits on this: the bare flag drives its banner (shown whenever the read is
  // currently failing), while the "fatal" flag — isError with nothing retained either
  // way — is what additionally hides the section's own content, the same distinction
  // OperationsTab's `hasFailed` and its independent `trades.length > 0` already draw.
  const positionsUnavailable = positionsQuery.isError;
  const positionsFatal = positionsQuery.isError && positionsQuery.data == null;
  const tradesUnavailable = externalTradesQuery.isError;

  // The summary leg is fatal only when there is nothing showable at all — not even a
  // last-good figure from another currency. While figures are on screen, a failed
  // request is reported by the inline currency note, never by a card claiming the
  // summary is unavailable directly above a summary that is visibly displayed.
  const summaryFatal = summaryQuery.isError && summary == null;

  // The stop is a figure the reader reads, so it is written the way every other number on
  // this page is written rather than as a bare "5000" — five thousand is `5.000` to a `pt`
  // reader. The console's own language is the locale, never the browser's, which answers a
  // question about the reader's browser instead of about the language they are reading.
  const listedPositionsLimit = MAX_LISTED_POSITIONS.toLocaleString(language);

  // Each section waits for its own read, never for the page's slowest one. The positions
  // read walks the cursor to the end of the portfolio, so it now takes as many round
  // trips as the portfolio is large; a single page-wide gate would hold the NAV card and
  // the Operations blotter — neither of which depends on a position row — behind up to
  // 51 requests, and a reader who opened the page for the blotter would wait for all of
  // them. Each flag below is true only for the very first load of a not-yet-cached
  // portfolio; a later refresh (retry, a recorded trade, a settled snapshot) re-fetches
  // in the background, and the cache is what keeps that from blanking the section again.
  //
  // The table and the donut wait for two reads, because a weight comes from the summary
  // and a row from the positions read: showing rows before the weights land would print
  // a column of dashes and then reorder the table under the reader.
  const summaryLoading = summaryQuery.isLoading;
  const positionsLoading = positionsQuery.isLoading;
  const positionsTableLoading = positionsLoading || summaryLoading;
  const tradesInitialLoading = externalTradesQuery.isLoading;
  const tradesReloading = externalTradesQuery.isFetching && !tradesInitialLoading;

  const portfolioFatal = portfolioQuery.isError && portfolioQuery.data == null;
  const portfolioDegraded = portfolioQuery.isError && !portfolioFatal;
  // Mirrors OperationsTab's own `acceptsTrades`: b-cell-position refuses a position
  // snapshot the same way it refuses a trade — `validate_portfolio_access` rejects both
  // `POST /position-snapshot` and `POST /external-trade` with `portfolio_not_active` on
  // a non-active portfolio. The parser's own validator only checks accessibility, so a
  // bulk import would otherwise be accepted up front and rejected row by row later.
  const portfolioActive = portfolio?.status === "active";
  const error = !portfolioId
    ? t("portfolio.notFound")
    : portfolioFatal
      ? t("portfolio.loadFailed")
      : (portfolioQuery.data === null && !portfolioQuery.isPending)
        ? t("portfolio.notFound")
        : null;

  // The last successfully-fetched currency — `summary` already carries the last-good
  // response regardless of which currency it was requested in, so a failed request
  // reads back from it rather than from whatever is currently (failing to) load. This is
  // also what "reverts" the toggle's pressed state on a rejected switch: `requestedCurrency`
  // (the query's key) is free to stay on the currency that failed, because everything the
  // user reads — the figure, and which button looks selected — is driven by this instead.
  const displayedCurrency = summary?.consolidated_nav?.currency;
  // `requestedCurrency` is only trustworthy while it names a genuinely new attempt — a
  // fetch in flight for a key that has not already failed. `isFetching` alone is not that
  // signal: it is also true while a *retry* of an already-failing currency runs in the
  // background (the toggle's own retry, or the bare refresh button, both call
  // `summaryQuery.refetch()` without changing `requestedCurrency`), and TanStack keeps
  // `isError` true for that whole retry. Without the `!isError` guard, that retry flips
  // `effectiveCurrency` back to the still-failing currency, blanking the chart into a
  // fresh full cursor walk for a currency the page is not even showing, only to revert
  // once the retry itself settles.
  const effectiveCurrency = summaryQuery.isFetching && !summaryQuery.isError
    ? requestedCurrency
    : (displayedCurrency ?? seededCurrency);
  const failedCurrency = summaryQuery.isError ? requestedCurrency : null;

  // Keyed on the currency the header is actually showing, so the toggle above reloads the
  // chart, and a currency switch the summary rejected never leaves the chart asking for a
  // currency the figures beside it could not show.
  const navHistoryQuery = usePortfolioValuationQuery(
    sessionId, accountScope, portfolioId, navHistoryPeriod, effectiveCurrency,
  );
  // The note is about a *switch* that did not land — it has nothing to add when the
  // currency that is failing to refresh is the same one already on screen (the retained
  // figures already answer that silently), so it only speaks up when the two disagree.
  const showsCurrencyFailedNote = failedCurrency != null && failedCurrency !== displayedCurrency;

  // A currency that already failed is still reachable: clicking it again is a genuine
  // retry, not a no-op, even though the query's key does not change. Clicking a
  // currency that is not currently failing and is already the one requested stays a
  // no-op, same as before caching.
  const changeNavCurrency = (currency: string) => {
    if (currency === requestedCurrency) {
      if (summaryQuery.isError) summaryQuery.refetch();
      return;
    }
    setChosenCurrency(currency);
  };

  // Sorting the blotter or revealing cancelations reloads the trades alone: the NAV, the
  // positions and the portfolio itself did not change, and refetching them would blank
  // three sections to answer a question about one. Changing the query's key is the whole
  // reload — no manual sequencing is needed, since each response is cached against the
  // key it answered rather than against "whichever request resolves last".
  const applyBlotterQuery = (query: BlotterQuery) => {
    setBlotterQuery(query);
  };

  // The full refresh, replaced under caching by invalidating every key — an active
  // portfolio, positions, or trades query refetches immediately regardless of which
  // blotter sort it is currently keyed on. `tradesKey` narrows that last leg to one exact
  // key instead of the whole portfolio-wide prefix — see `recordTrades` for why.
  //
  // This never touches the booking leg. `useBookingStatus` polls on its own clock; a
  // retry button here (positions, summary) must not re-arm that clock, and `onProgress`
  // must not be able to trigger its own re-check through this function and chase its own
  // tail. Only a reader action that can actually change the book — an import settling, a
  // declaration, a recorded trade — calls `booking.check()` itself, once, alongside this
  // refresh; see `csvImport`'s `onImported`, `snapshotDeclared`, and `recordTrades` below.
  //
  // The summary leg needs one more step whenever the currency currently keyed is the one
  // that failed: invalidating that same key would only re-send the request that never
  // answered, which is the exact trap this refresh exists to escape. Ask directly for the
  // currency the figures are actually showing instead — a `refetchQueries` on that precise
  // key, not the currently-active one, so it does not race the render that has not yet
  // picked it up — then hand the query over to it. Only that leg is special-cased; the
  // other three still go through the ordinary invalidate.
  const refreshPortfolioDetail = (options?: { tradesKey?: readonly unknown[] }) => {
    if (!portfolioId) return;
    queryClient.invalidateQueries({ queryKey: portfolioDetailQueryKeys.portfolio(portfolioId) });
    queryClient.invalidateQueries({ queryKey: portfolioDetailQueryKeys.positions(portfolioId) });
    queryClient.invalidateQueries({
      queryKey: options?.tradesKey ?? portfolioDetailQueryKeys.externalTradesByPortfolio(portfolioId),
    });
    queryClient.invalidateQueries({ queryKey: portfolioDetailQueryKeys.navHistoryByPortfolio(portfolioId) });
    if (summaryQuery.isError) {
      const workingCurrency = summary?.consolidated_nav?.currency ?? seededCurrency ?? DEFAULT_NAV_CURRENCY;
      queryClient.refetchQueries({ queryKey: portfolioDetailQueryKeys.summary(portfolioId, workingCurrency) });
      setChosenCurrency(workingCurrency);
    } else {
      queryClient.invalidateQueries({ queryKey: portfolioDetailQueryKeys.summaryByPortfolio(portfolioId) });
    }
  };

  // `snapshotDeclared` below awaits a poll that can run up to 30s, so the function it
  // eventually calls must not be the one closed over at declare-time — that copy's own
  // closed-over `summaryQuery`/`summary` are frozen to whatever they were the moment the
  // dialog was opened, not to what they are by the time the poll actually settles. Reading
  // through a ref keeps the call reading the latest render's function instead.
  const refreshPortfolioDetailRef = useRef(refreshPortfolioDetail);
  refreshPortfolioDetailRef.current = refreshPortfolioDetail;

  // `booking` is declared after `csvImport` below, so `onImported` cannot close over
  // `booking.check` directly at definition time — it reads it through this ref instead,
  // the same reason `refreshPortfolioDetailRef` exists, and for the same three call sites
  // documented on `refreshPortfolioDetail` above.
  const bookingCheckRef = useRef<() => void>(() => {});

  // Each entry button, kept by its tab. Radix unmounts a `TabsContent` whose tab is not
  // selected, so only the active tab's entry is ever non-null. `activeEntryButton` reads
  // it at Close time, after the render that `activeTab` (declared below) belongs to.
  const entryButtonByTabRef = useRef<Record<PortfolioTab, HTMLButtonElement | null>>({
    positions: null,
    operations: null,
  });

  // The whole CSV import pipeline, called once here — above the `Tabs` below — so an
  // in-flight import survives a tab switch. This replaces `forceMount`: the old design
  // kept the pipeline alive by never unmounting its tab; this one keeps it alive by
  // never storing its state inside a tab in the first place. See `useCsvImport` for the
  // portfolio-change guard that replaces the old `key={portfolioId}` remount.
  const csvImport = useCsvImport({
    portfolioId,
    sessionId,
    accountScope,
    onImported: () => {
      refreshPortfolioDetailRef.current();
      bookingCheckRef.current();
    },
    activeEntryButton: () => entryButtonByTabRef.current[activeTab],
  });

  // Whether the figures on this page are final, and the poll that watches them settle.
  // Progress — a tick whose counts fell, or whose page ids changed — reloads the same
  // figures `refreshPortfolioDetail` reloads, through the ref for the same reason
  // `snapshotDeclared` reads it. It never re-checks booking itself; the poll keeps its
  // own clock regardless of how often the figures around it refresh.
  const booking = useBookingStatus({
    sessionId,
    accountScope,
    portfolioId,
    onProgress: () => refreshPortfolioDetailRef.current(),
  });
  bookingCheckRef.current = booking.check;

  // Controlled so the tab can follow the open import: a `switchImportType` from the
  // panel moves to its own tab. Adjusted during render, not in an effect, so the page
  // never paints one frame with the tab and the panel naming different types. Opening
  // from the current tab, or a re-type the tab itself caused, lands on the tab already
  // selected, so this never moves the tab away from where the user put it.
  const [activeTab, setActiveTab] = useState<PortfolioTab>("positions");
  const [followedImport, setFollowedImport] = useState<ParseRequestType | null>(null);
  if (csvImport.activeImport !== followedImport) {
    setFollowedImport(csvImport.activeImport);
    if (csvImport.activeImport != null) setActiveTab(TAB_BY_IMPORT_TYPE[csvImport.activeImport]);
  }
  const changeTab = (value: string) => {
    if (!isPortfolioTab(value)) return;
    setActiveTab(value);
    csvImport.retypeImport(IMPORT_TYPE_BY_TAB[value]);
  };

  // Recording a trade returns the blotter to its default order, so the row just added is
  // at the top rather than wherever a sort the user chose earlier happens to put it.
  //
  // That order change is itself a key change the trades query will act on by itself, so
  // invalidating the whole trades prefix here would additionally force a redundant fetch
  // of the order this page is about to leave — naming the exact key it is about to become
  // keeps this to the one fetch the new order actually needs.
  const recordTrades = () => {
    setBlotterQuery((current) => ({ ...current, order: DEFAULT_BLOTTER_ORDER }));
    if (portfolioId) {
      refreshPortfolioDetail({
        tradesKey: portfolioDetailQueryKeys.externalTrades(portfolioId, DEFAULT_BLOTTER_ORDER, blotterQuery.showsCanceled),
      });
      bookingCheckRef.current();
    }
  };

  // A declaration settles asynchronously: reconciliation issues an adjustment trade and
  // only its booking moves the position and the NAV. Poll the snapshot until it settles
  // (or a short deadline passes) before refreshing, so the reload the user sees already
  // carries the declared quantity rather than the state it was meant to replace.
  const waitForSettledSnapshot = async (snapshotId: string): Promise<PositionSnapshot | null> => {
    const deadline = Date.now() + SNAPSHOT_SETTLE_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await wait(SNAPSHOT_POLL_MS);
      if (!isMounted.current || !sessionId || !accountScope) return null;
      const snapshot = await getPositionSnapshotById(sessionId, accountScope, snapshotId)
        .catch(() => null);
      if (snapshot && SETTLED_SNAPSHOT_STATUSES.includes(snapshot.status)) return snapshot;
    }
    return null;
  };

  const snapshotDeclared = async (snapshotId: string) => {
    setSnapshotTarget(null);
    const settled = await waitForSettledSnapshot(snapshotId);
    if (!isMounted.current) return;
    if (settled?.status === "failed") {
      toast({ title: t("portfolio.snapshotFailed"), variant: "destructive" });
    } else if (!settled) {
      toast({ title: t("portfolio.snapshotStillProcessing") });
    }
    refreshPortfolioDetailRef.current();
    bookingCheckRef.current();
  };

  return (
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("portfolio.back")}
          onClick={() => navigate("/console/portfolio")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">
              {portfolio?.name ?? t("portfolio.title")}
            </h1>
            {portfolio && (
              <Badge
                variant={portfolio.status === "active" ? "default" : "secondary"}
                data-testid="portfolio-status"
              >
                {labelOr(t, `status.${portfolio.status}`, portfolio.status)}
              </Badge>
            )}
          </div>
          {portfolio && portfolio.unique_id && (
            <p
              className="text-xs text-muted-foreground font-mono"
              title={t("portfolio.uniqueIdLabel")}
            >
              <span className="sr-only">{t("portfolio.uniqueIdLabel")}: </span>
              {portfolio.unique_id}
            </p>
          )}
          <p className="text-muted-foreground">{t("portfolio.detailSubtitle")}</p>
        </div>
      </div>

      {scopeMissing && (
        <Card className="p-6 border-destructive" data-testid="portfolio-no-scope">
          <p className="text-destructive mb-4">{t("portfolio.noAccountScope")}</p>
          <Button variant="outline" onClick={exitToLogin}>
            {t("portfolio.signInAgain")}
          </Button>
        </Card>
      )}

      {!scopeMissing && error && (
        <Card className="p-6 border-destructive" data-testid="portfolio-load-failed">
          <p className="text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={() => refreshPortfolioDetail()} data-testid="portfolio-load-retry">
            {t("portfolio.retry")}
          </Button>
        </Card>
      )}

      {/* Only reachable once the portfolio has answered at least once: a refresh that
          fails afterward keeps that retained portfolio (the header above already reads
          off it) on screen, with this note standing in for the blocking card above. */}
      {!scopeMissing && portfolioDegraded && (
        <Card className="mb-4 p-4 border-destructive" data-testid="portfolio-refresh-failed">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-destructive text-sm">{t("portfolio.loadFailed")}</p>
            <Button variant="outline" size="sm" onClick={() => refreshPortfolioDetail()} data-testid="portfolio-refresh-retry">
              {t("portfolio.retry")}
            </Button>
          </div>
        </Card>
      )}

      {!scopeMissing && !error && (
        <>
          <NavHeader
            summary={summary}
            isLoading={summaryLoading}
            isRefreshing={summaryQuery.isFetching && !summaryLoading}
            currency={effectiveCurrency ?? DEFAULT_NAV_CURRENCY}
            onCurrencyChange={changeNavCurrency}
            booking={booking}
          />

          {!summaryLoading && showsCurrencyFailedNote && summary != null && (
            <p className="mt-2 text-sm text-destructive" data-testid="nav-currency-failed">
              {t("portfolio.navCurrencyFailed").replace("{attempted}", failedCurrency!)}
            </p>
          )}

          {/* The summary's own bare-failure banner, mirroring positions and trades: a
              failed refetch of the currency already on screen retains its figures and
              matches neither the mismatch note (same currency) nor the fatal card
              (figures showable) — without this line that failure would be silent and
              stale figures would read as current. */}
          {!summaryLoading && summaryQuery.isError && !summaryFatal && !showsCurrencyFailedNote && (
            <div className="mt-2 flex flex-wrap items-center gap-3" data-testid="portfolio-summary-refresh-failed">
              <p className="text-sm text-destructive">{t("portfolio.summaryRefreshFailed")}</p>
              <Button variant="outline" size="sm" onClick={() => summaryQuery.refetch()} data-testid="portfolio-summary-refresh-retry">
                {t("portfolio.retry")}
              </Button>
            </div>
          )}

          {!summaryLoading && summaryFatal && (
            <Card className="mt-4 p-6 border-destructive" data-testid="portfolio-summary-unavailable">
              <p className="text-destructive mb-4">{t("portfolio.summaryUnavailable")}</p>
              <Button variant="outline" onClick={() => refreshPortfolioDetail()} data-testid="portfolio-summary-retry">
                {t("portfolio.retry")}
              </Button>
            </Card>
          )}

          {/* Its own read with its own states, independent of the summary's: a failed
              summary hides the live "now" point and nothing else. The gate on booking is
              the same one `NavHeader` applies to its figure, so the chart never draws a
              history the card above has not yet called final. */}
          <NavHistoryChart
            series={navHistoryQuery.series}
            isLoading={navHistoryQuery.isLoading}
            isFetching={navHistoryQuery.isFetching}
            isError={navHistoryQuery.isError}
            error={navHistoryQuery.error}
            onRetry={() => navHistoryQuery.refetch()}
            period={navHistoryPeriod}
            onPeriodChange={setNavHistoryPeriod}
            currency={effectiveCurrency ?? DEFAULT_NAV_CURRENCY}
            summary={summaryFatal ? null : summary}
            awaitsBooking={!booking.hasAnswered}
          />

          <div className="mt-6 space-y-6">
            {/* Rendered once, above the tabs: Radix unmounts a `TabsContent` the moment
                its tab is not selected, which used to strand a running import mid-upload
                with no way to see, close, or replace it the instant the user switched
                tabs. Before the upload starts, a tab switch re-types the panel to that
                tab's import (`changeTab`); after it starts, the panel keeps its type and
                stays visible on both tabs. Both entry buttons stay in their own tabs —
                only the panel itself moved. */}
            {csvImport.activeImport != null && <ImportPanel importState={csvImport} />}

            <Tabs value={activeTab} onValueChange={changeTab}>
              <TabsList>
                <TabsTrigger value="positions" data-testid="tab-positions">{t("portfolio.tab.positions")}</TabsTrigger>
                <TabsTrigger value="operations" data-testid="tab-operations">{t("portfolio.tab.operations")}</TabsTrigger>
              </TabsList>
              <TabsContent value="positions" className="space-y-6 mt-4">
                {/* Always shown, independent of `positionsFatal` below: the import has no
                    dependency on positions having loaded, and a failed positions read is
                    exactly when a user is likely to want to load some from a file. */}
                <div className="flex flex-wrap items-center justify-end gap-3">
                  {portfolio && !portfolioActive && (
                    <p className="mr-auto text-sm text-muted-foreground" data-testid="portfolio-snapshot-import-blocked">
                      {t("portfolio.snapshotsBlocked")}
                    </p>
                  )}
                  <Button
                    ref={(button) => {
                      entryButtonByTabRef.current.positions = button;
                    }}
                    variant="outline"
                    // A disabled native button also drops focus to <body> the instant this
                    // very click disables it. `aria-disabled` plus a no-op keeps it focusable
                    // and keyboard-reachable instead — see `ImportPanel`, which moves focus
                    // into the panel that opens right after.
                    aria-disabled={csvImport.activeImport != null || !portfolioActive}
                    onClick={(event) => {
                      if (csvImport.activeImport != null || !portfolioActive) return;
                      csvImport.openImport(IMPORT_TYPE_BY_TAB.positions, event.currentTarget);
                    }}
                    className={csvImport.activeImport != null || !portfolioActive ? "opacity-50" : undefined}
                    data-testid="import-open-position-snapshot"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t("portfolio.import.open.position-parse-request")}
                  </Button>
                </div>
                {/* Renders only while the book is settling, slow, timed out or unknown —
                    above the table it qualifies, and independent of the positions read,
                    since the one case it exists for is a table with nothing in it yet. */}
                <BookingMarker status={booking} size="full" />
                {/* Shown on any positions failure, retained data or not — same shape as
                    OperationsTab's own banner. Only a *fatal* failure (nothing retained)
                    additionally hides the donut and the table below. */}
                {!positionsLoading && positionsUnavailable && (
                  <Card className="p-6 border-destructive" data-testid="portfolio-positions-unavailable">
                    <p className="text-destructive mb-4">{t("portfolio.positionsUnavailable")}</p>
                    <Button variant="outline" onClick={() => refreshPortfolioDetail()} data-testid="portfolio-positions-retry">
                      {t("portfolio.retry")}
                    </Button>
                  </Card>
                )}
                {!positionsFatal && (
                  <>
                    {/* Held back until both reads land: with the page-wide gate gone, the
                        donut would otherwise read an empty `allocations` while the summary
                        is still in flight and tell the reader prices are on their way. */}
                    {!positionsTableLoading && (
                      <AllocationDonut
                        positions={positions}
                        summary={summary}
                        summaryUnavailable={summaryFatal}
                      />
                    )}
                    {/* Above the table, not below it: in the only case it fires there are
                        5,000 rows between the two, and a qualification the reader meets
                        after the ranking it qualifies is not a qualification. Gated on the
                        table's own readiness, so it never stands over a skeleton — the
                        positions leg can land while the summary is still in flight. */}
                    {!positionsTableLoading && positionsTruncated && (
                      <p className="text-sm text-muted-foreground" data-testid="portfolio-truncated">
                        {t("portfolio.truncatedPositions").replace("{limit}", listedPositionsLimit)}
                      </p>
                    )}
                    {/* Keyed on the portfolio: React reuses this instance across a
                        param-only route change, and the previous portfolio's chosen sort
                        would otherwise open the next one. */}
                    <PositionsTable
                      key={portfolioId}
                      positions={positions}
                      summary={summary}
                      isLoading={positionsTableLoading}
                      onDeclare={setSnapshotTarget}
                      booking={booking}
                    />
                  </>
                )}
              </TabsContent>
              <TabsContent value="operations" className="mt-4 space-y-4">
                <OperationsTab
                  portfolioId={portfolioId!}
                  portfolioStatus={portfolio?.status}
                  trades={trades}
                  isLoading={tradesInitialLoading}
                  isReloading={tradesReloading}
                  isTruncated={tradesTruncated}
                  order={blotterQuery.order}
                  onOrderChange={(order) => applyBlotterQuery({ ...blotterQuery, order })}
                  showsCanceled={blotterQuery.showsCanceled}
                  onShowsCanceledChange={(showsCanceled) =>
                    applyBlotterQuery({ ...blotterQuery, showsCanceled })}
                  onTradesRecorded={recordTrades}
                  hasFailed={!tradesInitialLoading && tradesUnavailable}
                  onRetry={() => externalTradesQuery.refetch()}
                  isImportOpen={csvImport.activeImport != null}
                  onOpenImport={(trigger) => csvImport.openImport(IMPORT_TYPE_BY_TAB.operations, trigger)}
                  importButtonRef={(button) => {
                    entryButtonByTabRef.current.operations = button;
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          <DeclareSnapshotDialog
            open={snapshotTarget != null}
            onOpenChange={(open) => {
              if (!open) setSnapshotTarget(null);
            }}
            portfolioId={portfolioId!}
            position={snapshotTarget}
            onDeclared={snapshotDeclared}
          />
        </>
      )}
    </main>
  );
};

export default PortfolioDetail;
